import { useState, useCallback, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import {
  MatchMode,
  OrchestrationCondition,
  OrchestrationCriteria,
  getConditionType,
  ConditionType,
} from "../domain/orchestrationCriteria.entity";

export function useCriteriaManager(
  form: UseFormReturn<any>,
  criteriaFieldName: string = "criteria"
) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const watchedCriteria = form.watch(criteriaFieldName);
  const conditions = useMemo(
    () => (watchedCriteria as OrchestrationCriteria | undefined)?.conditions || [],
    [watchedCriteria]
  );

  const handleOpenAdd = useCallback(() => {
    setEditingIndex(null);
    setModalOpen(true);
  }, []);

  const handleEdit = useCallback((index: number) => {
    setEditingIndex(index);
    setModalOpen(true);
  }, []);

  const handleDelete = useCallback(
    (index: number) => {
      const currentCriteria = (form.getValues(criteriaFieldName) as OrchestrationCriteria) || {
        matchMode: MatchMode.ALL,
        conditions: [],
      };
      const newConditions = [...conditions];
      newConditions.splice(index, 1);
      form.setValue(criteriaFieldName, {
        ...currentCriteria,
        conditions: newConditions,
      });
    },
    [conditions, form, criteriaFieldName]
  );

  const handleSave = useCallback(
    (condition: OrchestrationCondition) => {
      const currentCriteria = (form.getValues(criteriaFieldName) as OrchestrationCriteria) || {
        matchMode: MatchMode.ALL,
        conditions: [],
      };
      const newConditions = [...conditions];
      if (editingIndex !== null) {
        newConditions[editingIndex] = condition;
      } else {
        newConditions.push(condition);
      }
      form.setValue(criteriaFieldName, {
        ...currentCriteria,
        conditions: newConditions,
      });
      setModalOpen(false);
      setEditingIndex(null);
    },
    [conditions, editingIndex, form, criteriaFieldName]
  );

  const handleClose = useCallback(() => {
    setModalOpen(false);
    setEditingIndex(null);
  }, []);

  const existingConditionTypes = useMemo(
    () => conditions.map(c => getConditionType(c)).filter((t): t is ConditionType => t !== null),
    [conditions]
  );

  const editingCondition = editingIndex !== null ? conditions[editingIndex] : undefined;

  return {
    conditions,
    modalOpen,
    editingIndex,
    editingCondition,
    existingConditionTypes,
    handleOpenAdd,
    handleEdit,
    handleDelete,
    handleSave,
    handleClose,
  };
}
